import React, { useMemo } from 'react';
import { Sidebar, SidebarBottomSection, SidebarDivider, SidebarHelp, SidebarIcon, SidebarLogo, SidebarLogout, SidebarTopSection, SidebarUserPreferences } from './Sidebar';
import Cog from '../../../Assets/Icons/settings.svg?react';
import PieChart from '../../../Assets/Icons/pie-chart.svg?react';
import CarCog from '../../../Assets/Icons/check-in.svg?react';
import UserIcon from '../../../Assets/Icons/user.svg?react';
import KeyManagement from '../../../Assets/Icons/key-management.svg?react';
import QRCode from '../../../Assets/Icons/qr-code.svg?react';
import { useHasViewAccess, useUserState } from '../../../Contexts/User';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppRoutes, PathName, UserRoutes } from '../../../CommonUtils/Routes';
import { UserPermissions } from '../../../API/Enums';
import { useLocalization } from '../../../Contexts/Localization';
import css from './Sidebar.module.scss';
import { useIsTablet } from '../../../CommonUtils/MediaQueries';

export const DashboardSidebar: React.FC = () => {
    const Localization = useLocalization();
    const hasViewAccess = useHasViewAccess();
    const location = useLocation();
    const navigate = useNavigate();
    const isTablet = useIsTablet();
    const { UserData } = useUserState();

    const summaryRouteActive: boolean = location.pathname.includes(AppRoutes.Summary);
    const activityRouteActive: boolean = location.pathname.includes(AppRoutes.Activity);
    const userRouteActive: boolean = location.pathname.includes(AppRoutes.Users);
    const settingsRouteActive: boolean = location.pathname.includes(AppRoutes.Settings);
    const keyManagementRouteActive: boolean = location.pathname.includes(AppRoutes.KeyManagement);
    const qrCheckInRouteActive: boolean = location.pathname.includes(AppRoutes.QRCodeCheckIn);

    const summaryDashboardAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.SummaryDashboardAccess)
            || hasViewAccess(UserPermissions.SummaryDashboard_CompareView)
            || hasViewAccess(UserPermissions.SummaryDashboard_BrandView);
    }, [hasViewAccess]);

    const activityDashboardAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.ActivityDashboardAccess);
    }, [hasViewAccess]);

    const userManagementAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.UserManagementAccess);
    }, [hasViewAccess]);

    const settingsAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.Settings_GeneralSettings)
            || hasViewAccess(UserPermissions.Settings_DisclaimersAndConsents)
            || hasViewAccess(UserPermissions.Settings_Communications);
    }, [hasViewAccess]);

    const keyManagementAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.KeyManagement_Mobile) && (UserData.is_staff || UserData.show_key_management_menu);
    }, [hasViewAccess, UserData.is_staff, UserData.show_key_management_menu]);

    const qrCodeCheckInAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.QRCodeScan_Mobile) && (UserData.is_staff || UserData.show_qr_code_scan_menu);
    }, [hasViewAccess, UserData.is_staff, UserData.show_qr_code_scan_menu]);

    return (
        <Sidebar>
            <SidebarUserPreferences />
            <SidebarDivider />
            <SidebarTopSection>
                <SidebarIcon
                    show={!isTablet && summaryDashboardAccess}
                    active={summaryRouteActive}
                    tooltip={Localization.Summary}
                    onClick={() => navigate(AppRoutes.Summary)}
                    icon={<PieChart className={css.icon} />}
                />
                <SidebarIcon
                    show={isTablet || activityDashboardAccess}
                    disabled={!activityDashboardAccess}
                    active={activityRouteActive}
                    tooltip={Localization.Activity}
                    onClick={() => navigate(AppRoutes.Activity)}
                    icon={<CarCog className={css.icon} />}
                    extraTabletPadding
                />
                <SidebarIcon
                    show={!isTablet && userManagementAccess}
                    active={userRouteActive}
                    tooltip={Localization.Users}
                    disableClickWhenActive={false}
                    onClick={() => {
                        if (!location.pathname.includes(PathName(AppRoutes.Users, UserRoutes.Maintenance))) {
                            navigate(AppRoutes.Users);
                        }
                    }}
                    icon={<UserIcon className={css.icon} />}
                />
                <SidebarIcon
                    show={!isTablet && settingsAccess}
                    active={settingsRouteActive}
                    disableClickWhenActive={false}
                    tooltip={Localization.Settings}
                    onClick={() => navigate(AppRoutes.Settings)}
                    colorPathStroke
                    icon={<Cog className={css.icon} />}
                />
                <SidebarIcon
                    show={isTablet}
                    disabled={!keyManagementAccess}
                    active={keyManagementRouteActive}
                    tooltip={Localization.KeyManagement}
                    onClick={() => navigate(AppRoutes.KeyManagement)}
                    icon={<KeyManagement className={css.icon} />}
                    colorPathFill
                    extraTabletPadding
                />
                <SidebarIcon
                    show={isTablet}
                    disabled={!qrCodeCheckInAccess}
                    active={qrCheckInRouteActive}
                    tooltip={Localization.QRCodeScan}
                    onClick={() => navigate(AppRoutes.QRCodeCheckIn)}
                    icon={<QRCode className={css.icon} />}
                    colorPathFill
                    extraTabletPadding
                />
                <SidebarHelp />
            </SidebarTopSection>
            <SidebarDivider />
            <SidebarBottomSection>
                <SidebarLogout />
                <SidebarDivider />
                <SidebarLogo />
            </SidebarBottomSection>
        </Sidebar>
    );
};